/**
 * Página pública de la Agenda de un evento.
 *
 * Ruta: `/agenda/:eventId`
 * Sin autenticación. Lee solo los campos públicos del evento (nombre,
 * fechas, ubicación, agenda) vía la Cloud Function `getPublicEventAgenda`.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, AlertCircle, Loader2 } from "lucide-react";
import AgendaTimeline from "@/components/agenda/AgendaTimeline";

interface PublicAgendaBlock {
  id: string;
  startTime: string;
  endTime: string;
  title: string;
  description?: string;
  location?: string;
}

interface PublicAgendaDay {
  id: string;
  date: string;
  dayCoordinator?: string;
  blocks: PublicAgendaBlock[];
}

interface PublicAgendaResponse {
  id: string;
  name: string;
  startDate: number;
  endDate: number;
  location: {
    name: string;
    address: string;
    city: string;
    department?: string;
  };
  organizerName?: string;
  coverImageUrl?: string;
  agenda: {
    days: PublicAgendaDay[];
    version: number;
    updatedAt: number | null;
  } | null;
}

function buildFunctionUrl(eventId: string): string {
  return `/api/public-event-agenda?eventId=${encodeURIComponent(eventId)}`;
}

function formatDate(date: Date, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...opts,
  }).format(date);
}

export default function PublicEventAgendaPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [data, setData] = useState<PublicAgendaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      setError("Falta el identificador del evento");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(buildFunctionUrl(eventId))
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${r.status}`);
        }
        return (await r.json()) as PublicAgendaResponse;
      })
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message || "No se pudo cargar la agenda");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const sortedDays = useMemo(() => {
    if (!data?.agenda?.days) return [];
    return [...data.agenda.days].sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-md w-full bg-white border-2 border-red-100 rounded-2xl p-6 shadow-lg text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-1">
            No se pudo cargar la agenda
          </h1>
          <p className="text-sm text-gray-600">
            {error || "El evento no existe o no es público."}
          </p>
          <Link
            to="/"
            className="inline-block mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    );
  }

  const start = data.startDate ? new Date(data.startDate) : null;
  const end = data.endDate ? new Date(data.endDate) : null;
  const dateRange =
    start && end
      ? start.toDateString() === end.toDateString()
        ? formatDate(start)
        : `${formatDate(start, { year: undefined })} — ${formatDate(end)}`
      : "";

  const hasAgenda = sortedDays.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-white">
      {/* Header — con imagen del evento de fondo + overlay para legibilidad */}
      <header className="relative overflow-hidden text-white">
        {data.coverImageUrl ? (
          <>
            {/* Imagen difuminada que cubre todo el fondo (sin franjas) */}
            <img
              src={data.coverImageUrl}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-purple-700/90 via-purple-700/80 to-indigo-700/90" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-600" />
        )}

        <div className="relative max-w-3xl mx-auto px-4 py-6 sm:py-8">
          <div className="flex items-center gap-2 text-purple-100 text-[11px] font-semibold uppercase tracking-widest mb-3">
            <Calendar className="w-3.5 h-3.5" />
            Agenda del campamento
          </div>

          <div className="flex items-start gap-4">
            {data.coverImageUrl && (
              <img
                src={data.coverImageUrl}
                alt={data.name}
                className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-white/30 shadow-xl shadow-black/20"
              />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight drop-shadow-sm">
                {data.name}
              </h1>
              {data.organizerName && (
                <p className="text-purple-100 text-sm mt-1">
                  {data.organizerName}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {dateRange && (
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-medium">
                <Calendar className="w-4 h-4 text-purple-100" />
                {dateRange}
              </span>
            )}
            {data.location?.name && (
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-medium">
                <MapPin className="w-4 h-4 text-purple-100" />
                {data.location.name}
                {data.location.city ? ` · ${data.location.city}` : ""}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        {!hasAgenda ? (
          <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              Aún no hay agenda publicada para este evento.
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Vuelve a consultar más tarde.
            </p>
          </div>
        ) : (
          <AgendaTimeline days={sortedDays} />
        )}

        <footer className="mt-10 flex flex-col items-center gap-2 text-center text-xs text-gray-400">
          <p>Agenda compartida públicamente · Solo lectura</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-semibold text-purple-600 hover:text-purple-700"
          >
            <img
              src="/branding/insignia_icon.svg"
              alt=""
              aria-hidden
              className="w-4 h-4"
            />
            Insignia
          </Link>
        </footer>
      </main>
    </div>
  );
}
